import { useState } from "react";
import { useFetch } from "../../../common/hooks/useFetch";
import Swal from "sweetalert2";
import moment from "moment";
import { saveFile } from "../../../common/utils/saveFile.util";
import { createReportAccidentService } from "../services/createReportAccident.service";
import { updateReportAccidentService } from "../services/updateReportAccident.service";
import { deleteReportAccidentService } from "../services/deleteReportAccident.service";
import { exportExcelReportAccidentService } from "../services/exportExcelReportAccident.service";

const initialStateAccident = {
  dateAccident: moment().format("YYYY-MM-DD"),
  namePersonInvolved: "",
  documentPersonInvolved: "",
  vehiclePlate: "",
  typeContract: "",
  placeOfEvent: "",
  fatality: "",
  seriousInjuries: "",
  minorInjuries: "",
  simpleShock: "",
  ipat: null,
  ipatAuthor: null,
};

export const useReportAccidentCommand = (refresh) => {
  const fetchData = useFetch();
  const [errors, setErrors] = useState({ hasErrors: false, errors: {} });
  const [accident, setAccident] = useState(initialStateAccident);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChangeAccident = ({ name, value }) => {
    setAccident((prev) => {
      return { ...prev, [name]: value };
    });
  };

  const createReportAccident = async (reportAccident) => {
    try {
      const isValid = validations(reportAccident);
      if (!isValid) return;
      if (isSubmitting) return;
      setIsSubmitting(true);
      const response = await createReportAccidentService(fetchData)(
        reportAccident
      );
      refresh?.();
      Swal.fire({
        icon: "success",
        title: "Siniestro registrado",
        text: response.message,
      });
      setAccident(initialStateAccident);
      setErrors({ hasErrors: false, errors: {} });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Ocurrió un error al registrar el siniestro",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const exportExcel = async (filters) => {
    try {
      const response = await exportExcelReportAccidentService(fetchData)(
        filters
      );
      saveFile(response, "reporte_siniestros_viales.xlsx");
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.message || "Ocurrió un error al exportar el reporte",
      });
    }
  };

  const updateReportAccident = async (reportAccident) => {
    try {
      const isValid = validations(reportAccident);
      if (!isValid) return;
      if (isSubmitting) return;
      setIsSubmitting(true);
      const response = await updateReportAccidentService(fetchData)(reportAccident);
      refresh?.();
      Swal.fire({
        icon: "success",
        title: "Siniestro actualizado",
        text: response.message,
      });
      setAccident(initialStateAccident);
      setErrors({ hasErrors: false, errors: {} });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Ocurrió un error al actualizar el siniestro",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const deleteReportAccident = async (id) => {
    try {
      const isSure = await Swal.fire({
        title: "¿Estás seguro?",
        text: "¿Deseas eliminar este reporte de siniestro?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Sí, eliminar",
        cancelButtonText: "Cancelar",
      });
      if (!isSure.isConfirmed) return;
      setIsSubmitting(true);
      await deleteReportAccidentService(fetchData)(id);
      refresh?.();
      Swal.fire({
        icon: "success",
        title: "Eliminado",
        text: "El reporte ha sido eliminado exitosamente",
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.message || "Ocurrió un error al eliminar el reporte",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const validations = (reportAccident) => {
    const newErrors = {};

    if (!reportAccident.dateAccident) {
      newErrors.dateAccident = "La fecha del siniestro es requerida";
    }

    if (!reportAccident.namePersonInvolved) {
      newErrors.namePersonInvolved = "El nombre del implicado es requerido";
    }

    if (!reportAccident.documentPersonInvolved) {
      newErrors.documentPersonInvolved = "La cédula del implicado es requerida";
    }

    if (!reportAccident.vehiclePlate) {
      newErrors.vehiclePlate = "La placa del vehículo es requerida";
    }

    if (!reportAccident.typeContract) {
      newErrors.typeContract = "El tipo de contrato es requerido";
    }

    if (!reportAccident.placeOfEvent) {
      newErrors.placeOfEvent = "El lugar del evento es requerido";
    }

    if (!reportAccident.fatality) {
      newErrors.fatality = "Las fatalidades son requeridas";
    }

    if (!reportAccident.seriousInjuries) {
      newErrors.seriousInjuries = "Las lesiones graves son requeridas";
    }

    if (!reportAccident.minorInjuries) {
      newErrors.minorInjuries = "Las lesiones leves son requeridas";
    }

    if (!reportAccident.simpleShock) {
      newErrors.simpleShock = "Los choques simples son requeridos";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors({ hasErrors: true, errors: newErrors });
      return false;
    }

    return true;
  };

  return {
    createReportAccident,
    updateReportAccident,
    deleteReportAccident,
    exportExcel,
    errors,
    handleChangeAccident,
    accident,
    setAccident,
    isSubmitting,
  };
};
